/**
 * Try to parse a JSON string a return it's javascript representation.
 * Returns an empty object if the parse fails
 * 
 * @param {string} json
 * @param {log.Logger} logger
 * @param {string} errorMsg
 * @return {*}
 */
export function parseJSON (json, logger, errorMsg) {
    let object = {};
    if(json !== undefined && json !== null && json !== ''){
        try {
            object = JSON.parse(json);
        } catch (e) {
            logger.warn(`${errorMsg}. Value received: ${json}`);
        }
    }
    return object;
}

/**
 * Contains the list of countries with their gps coordinates & zoom level to be centered on
 *
 * @param {string} countryCode 3 character country code (ISO-3166-1)
 * @param {number} degreeSize depending on the component size, the offset is dynamically computed
 * @returns {{offset: number[], scale: number}}
 */
export function getCountryProjection (countryCode, degreeSize) {

    // Source : https://github.com/eesur/country-codes-lat-long/blob/master/country-codes-lat-long-alpha3.json
    // data
    //     .map(c => ({"country": c.alpha3.toLowerCase(), "value": {"scale": 1, "offset": [c.longitude, c.latitude]}}))
    //     .reduce ((a, i) => {a[i.country] = i.value; return a}, {})
    const scales= {
        "alb":{"scale":40,"offset":[20,41]},
        "dza":{"scale":8,"offset":[3,28]},
        "asm":{"scale":12,"offset":[-170,-14.3333]},
        "and":{"scale":6,"offset":[1.6,42.5]},
        "ago":{"scale":8,"offset":[18.5,-12.5]},
        "aia":{"scale":26,"offset":[-63.1667,18.25]},
        "ata":{"scale":2,"offset":[0,-90]},
        "atg":{"scale":10,"offset":[-61.8,17.05]},
        "arg":{"scale":3,"offset":[-64,-34]},
        "arm":{"scale":40,"offset":[45,40]},
        "abw":{"scale":12,"offset":[-69.9667,12.5]},
        "aus":{"scale":4,"offset":[133,-27]},
        "aut":{"scale":35,"offset":[13.3333,47.3333]},
        "aze":{"scale":40,"offset":[47.5,40.5]},
        "bhs":{"scale":20,"offset":[-76,24.25]},
        "bhr":{"scale":8,"offset":[50.55,26]},
        "bgd":{"scale":25,"offset":[90,24]},
        "brb":{"scale":16,"offset":[-59.5333,13.1667]},
        "blr":{"scale":25,"offset":[28,53]},
        "bel":{"scale":30,"offset":[4,50.8333]},
        "blz":{"scale":60,"offset":[-88.75,17.25]},
        "ben":{"scale":20,"offset":[2.25,9.5]},
        "bmu":{"scale":16,"offset":[-64.75,32.3333]},
        "btn":{"scale":35,"offset":[90.5,27.5]},
        "bol":{"scale":10,"offset":[-65,-17]},
        "bih":{"scale":40,"offset":[18,44]},
        "bwa":{"scale":10,"offset":[24,-22]},
        "bvt":{"scale":30,"offset":[3.4,-54.4333]},
        "bra":{"scale":3,"offset":[-55,-10]},
        "iot":{"scale":30,"offset":[71.5,-6]},
        "brn":{"scale":7,"offset":[114.6667,4.5]},
        "bgr":{"scale":40,"offset":[25,43]},
        "bfa":{"scale":20,"offset":[-2,13]},
        "bdi":{"scale":70,"offset":[30,-3.5]},
        "khm":{"scale":30,"offset":[105,13]},
        "cmr":{"scale":10,"offset":[12,6]},
        "can":{"scale":3,"offset":[-95,60]},
        "cpv":{"scale":20,"offset":[-24,16]},
        "cym":{"scale":30,"offset":[-80.5,19.5]},
        "caf":{"scale":12,"offset":[21,7]},
        "tcd":{"scale":10,"offset":[19,15]},
        "chl":{"scale":3,"offset":[-71,-30]},
        "chn":{"scale":4,"offset":[105,35]},
        "cxr":{"scale":18,"offset":[105.6667,-10.5]},
        "cck":{"scale":20,"offset":[96.8333,-12.5]},
        "col":{"scale":10,"offset":[-72,4]},
        "com":{"scale":18,"offset":[44.25,-12.1667]},
        "cog":{"scale":18,"offset":[15,-1]},
        "cod":{"scale":5,"offset":[25,0]},
        "cok":{"scale":30,"offset":[-159.7667,-21.2333]},
        "cri":{"scale":45,"offset":[-84,10]},
        "civ":{"scale":22,"offset":[-5,8]},
        "hrv":{"scale":30,"offset":[15.5,45.1667]},
        "cub":{"scale":28,"offset":[-80,21.5]},
        "cyp":{"scale":70,"offset":[33,35]},
        "cze":{"scale":30,"offset":[15.5,49.75]},
        "dnk":{"scale":30,"offset":[10,56]},
        "dji":{"scale":50,"offset":[43,11.5]},
        "dma":{"scale":10,"offset":[-61.3333,15.4167]},
        "dom":{"scale":55,"offset":[-70.6667,19]},
        "ecu":{"scale":10,"offset":[-77.5,-2]},
        "egy":{"scale":13,"offset":[30,27]},
        "slv":{"scale":70,"offset":[-88.9167,13.8333]},
        "gnq":{"scale":45,"offset":[10,2]},
        "eri":{"scale":28,"offset":[39,15]},
        "est":{"scale":30,"offset":[26,59]},
        "eth":{"scale":6,"offset":[38,8]},
        "flk":{"scale":60,"offset":[-59,-51.75]},
        "fro":{"scale":20,"offset":[-7,62]},
        "fji":{"scale":20,"offset":[175,-18]},
        "fin":{"scale":13,"offset":[26,64]},
        "fra":{"scale":17,"offset":[2,46]},
        "guf":{"scale":10,"offset":[-53,4]},
        "pyf":{"scale":20,"offset":[-140,-15]},
        "atf":{"scale":10,"offset":[67,-43]},
        "gab":{"scale":25,"offset":[11.75,-1]},
        "gmb":{"scale":50,"offset":[-16.5667,13.4667]},
        "geo":{"scale":20,"offset":[43.5,42]},
        "deu":{"scale":20,"offset":[9,51]},
        "gha":{"scale":20,"offset":[-2,8]},
        "gib":{"scale":18,"offset":[-5.3667,36.1833]},
        "grc":{"scale":17,"offset":[22,39]},
        "grl":{"scale":5,"offset":[-40,72]},
        "grd":{"scale":20,"offset":[-61.6667,12.1167]},
        "glp":{"scale":16,"offset":[-61.5833,16.25]},
        "gum":{"scale":10,"offset":[144.7833,13.4667]},
        "gtm":{"scale":38,"offset":[-90.25,15.5]},
        "ggy":{"scale":8,"offset":[-2.56,49.5]},
        "gin":{"scale":20,"offset":[-10,11]},
        "gnb":{"scale":50,"offset":[-15,12]},
        "guy":{"scale":15,"offset":[-59,5]},
        "hti":{"scale":60,"offset":[-72.4167,19]},
        "hmd":{"scale":20,"offset":[72.5167,-53.1]},
        "vat":{"scale":5,"offset":[12.45,41.9]},
        "hnd":{"scale":35,"offset":[-86.5,15]},
        "hkg":{"scale":8,"offset":[114.1667,22.25]},
        "hun":{"scale":35,"offset":[20,47]},
        "isl":{"scale":20,"offset":[-18,65]},
        "ind":{"scale":5,"offset":[77,20]},
        "idn":{"scale":6,"offset":[120,-5]},
        "irn":{"scale":8,"offset":[53,32]},
        "irq":{"scale":18,"offset":[44,33]},
        "irl":{"scale":30,"offset":[-8,53]},
        "imn":{"scale":16,"offset":[-4.55,54.23]},
        "isr":{"scale":40,"offset":[34.75,31.5]},
        "ita":{"scale":13,"offset":[12.8333,42.8333]},
        "jam":{"scale":50,"offset":[-77.5,18.25]},
        "jpn":{"scale":8,"offset":[138,36]},
        "jey":{"scale":18,"offset":[-2.13,49.21]},
        "jor":{"scale":37,"offset":[36,31]},
        "kaz":{"scale":5,"offset":[68,48]},
        "ken":{"scale":6,"offset":[38,1]},
        "kir":{"scale":25,"offset":[173,1.4167]},
        "prk":{"scale":8,"offset":[127,40]},
        "kor":{"scale":8,"offset":[127.5,37]},
        "kwt":{"scale":50,"offset":[47.6581,29.3375]},
        "kgz":{"scale":20,"offset":[75,41]},
        "lao":{"scale":15,"offset":[105,18]},
        "lva":{"scale":38,"offset":[25,57]},
        "lbn":{"scale":50,"offset":[35.8333,33.8333]},
        "lso":{"scale":70,"offset":[28.5,-29.5]},
        "lbr":{"scale":38,"offset":[-9.5,6.5]},
        "lby":{"scale":10,"offset":[17,25]},
        "lie":{"scale":10,"offset":[9.5333,47.1667]},
        "ltu":{"scale":28,"offset":[24,56]},
        "lux":{"scale":15,"offset":[6.1667,49.75]},
        "mac":{"scale":18,"offset":[113.55,22.1667]},
        "mkd":{"scale":60,"offset":[22,41.8333]},
        "mdg":{"scale":10,"offset":[47,-20]},
        "mwi":{"scale":20,"offset":[34,-13.5]},
        "mys":{"scale":12,"offset":[112.5,2.5]},
        "mdv":{"scale":20,"offset":[73,3.25]},
        "mli":{"scale":10,"offset":[-4,17]},
        "mlt":{"scale":18,"offset":[14.5833,35.8333]},
        "mhl":{"scale":20,"offset":[168,9]},
        "mtq":{"scale":10,"offset":[-61,14.6667]},
        "mrt":{"scale":10,"offset":[-12,20]},
        "mus":{"scale":70,"offset":[57.55,-20.2833]},
        "myt":{"scale":20,"offset":[45.1667,-12.8333]},
        "mex":{"scale":7,"offset":[-102,23]},
        "fsm":{"scale":16,"offset":[158.25,6.9167]},
        "mda":{"scale":40,"offset":[29,47]},
        "mco":{"scale":8,"offset":[7.4,43.7333]},
        "mng":{"scale":10,"offset":[105,46]},
        "mne":{"scale":50,"offset":[19,42]},
        "msr":{"scale":10,"offset":[-62.2,16.75]},
        "mar":{"scale":7,"offset":[-5,32]},
        "moz":{"scale":9,"offset":[35,-18.25]},
        "mmr":{"scale":7,"offset":[98,22]},
        "nam":{"scale":10,"offset":[17,-22]},
        "nru":{"scale":14,"offset":[166.9167,-0.5333]},
        "npl":{"scale":22,"offset":[84,28]},
        "nld":{"scale":25,"offset":[5.75,52.5]},
        "ant":{"scale":20,"offset":[-68.75,12.25]},
        "ncl":{"scale":30,"offset":[165.5,-21.5]},
        "nzl":{"scale":10,"offset":[174,-41]},
        "nic":{"scale":35,"offset":[-85,13]},
        "ner":{"scale":10,"offset":[8,16]},
        "nga":{"scale":12,"offset":[8,10]},
        "niu":{"scale":12,"offset":[-169.8667,-19.0333]},
        "nfk":{"scale":14,"offset":[167.95,-29.0333]},
        "mnp":{"scale":26,"offset":[145.75,15.2]},
        "nor":{"scale":4,"offset":[10,62]},
        "omn":{"scale":15,"offset":[57,21]},
        "pak":{"scale":10,"offset":[70,30]},
        "plw":{"scale":30,"offset":[134.5,7.5]},
        "pse":{"scale":30,"offset":[35.25,32]},
        "pan":{"scale":30,"offset":[-80,9]},
        "png":{"scale":15,"offset":[147,-6]},
        "pry":{"scale":18,"offset":[-58,-23]},
        "per":{"scale":8,"offset":[-76,-10]},
        "phl":{"scale":8,"offset":[122,13]},
        "pcn":{"scale":20,"offset":[-127.4,-24.7]},
        "pol":{"scale":26,"offset":[20,52]},
        "prt":{"scale":30,"offset":[-8,39.5]},
        "pri":{"scale":70,"offset":[-66.5,18.25]},
        "qat":{"scale":50,"offset":[51.25,25.5]},
        "reu":{"scale":8,"offset":[55.6,-21.1]},
        "rou":{"scale":30,"offset":[25,46]},
        "rus":{"scale":10,"offset":[100,60]},
        "rwa":{"scale":70,"offset":[30,-2]},
        "shn":{"scale":16,"offset":[-5.7,-15.9333]},
        "kna":{"scale":10,"offset":[-62.75,17.3333]},
        "lca":{"scale":8,"offset":[-61.1333,13.8833]},
        "spm":{"scale":15,"offset":[-56.3333,46.8333]},
        "vct":{"scale":16,"offset":[-61.2,13.25]},
        "wsm":{"scale":70,"offset":[-172.3333,-13.5833]},
        "smr":{"scale":6,"offset":[12.4167,43.7667]},
        "stp":{"scale":16,"offset":[7,1]},
        "sau":{"scale":8,"offset":[45,25]},
        "sen":{"scale":30,"offset":[-14,14]},
        "srb":{"scale":38,"offset":[21,44]},
        "syc":{"scale":20,"offset":[55.6667,-4.5833]},
        "sle":{"scale":50,"offset":[-11.5,8.5]},
        "sgp":{"scale":6,"offset":[103.8,1.3667]},
        "svk":{"scale":40,"offset":[19.5,48.6667]},
        "svn":{"scale":50,"offset":[15,46]},
        "slb":{"scale":16,"offset":[159,-8]},
        "som":{"scale":6,"offset":[49,10]},
        "zaf":{"scale":4,"offset":[24,-29]},
        "sgs":{"scale":20,"offset":[-37,-54.5]},
        "esp":{"scale":15,"offset":[-4,40]},
        "lka":{"scale":25,"offset":[81,7]},
        "sdn":{"scale":10,"offset":[30,15]},
        "sur":{"scale":25,"offset":[-56,4]},
        "sjm":{"scale":20,"offset":[20,78]},
        "swz":{"scale":20,"offset":[31.5,-26.5]},
        "swe":{"scale":8,"offset":[15,62]},
        "che":{"scale":40,"offset":[8,47]},
        "syr":{"scale":30,"offset":[38,35]},
        "twn":{"scale":20,"offset":[121,23.5]},
        "tjk":{"scale":38,"offset":[71,39]},
        "tza":{"scale":10,"offset":[35,-6]},
        "tha":{"scale":8,"offset":[100,15]},
        "tls":{"scale":20,"offset":[125.5167,-8.55]},
        "tgo":{"scale":20,"offset":[1.1667,8]},
        "tkl":{"scale":16,"offset":[-172,-9]},
        "ton":{"scale":18,"offset":[-175,-20]},
        "tto":{"scale":8,"offset":[-61,11]},
        "tun":{"scale":20,"offset":[9,34]},
        "tur":{"scale":15,"offset":[35,39]},
        "tkm":{"scale":12,"offset":[60,40]},
        "tca":{"scale":20,"offset":[-71.5833,21.75]},
        "tuv":{"scale":20,"offset":[178,-8]},
        "uga":{"scale":36,"offset":[32,1]},
        "ukr":{"scale":16,"offset":[32,49]},
        "are":{"scale":30,"offset":[54,24]},
        "gbr":{"scale":12,"offset":[-2,54]},
        "usa":{"scale":2,"offset":[-97,38]},
        "umi":{"scale":8,"offset":[166.6,19.2833]},
        "ury":{"scale":20,"offset":[-56,-33]},
        "uzb":{"scale":10,"offset":[64,41]},
        "vut":{"scale":16,"offset":[167,-16]},
        "ven":{"scale":11,"offset":[-66,8]},
        "vnm":{"scale":11,"offset":[106,16]},
        "vgb":{"scale":16,"offset":[-64.5,18.5]},
        "vir":{"scale":18,"offset":[-64.8333,18.3333]},
        "wlf":{"scale":20,"offset":[-176.2,-13.3]},
        "esh":{"scale":8,"offset":[-13,24.5]},
        "yem":{"scale":20,"offset":[48,15]},
        "zmb":{"scale":10,"offset":[30,-15]},
        "zwe":{"scale":18,"offset":[30,-20]},
        "afg":{"scale":10,"offset":[65,33]}
    }

    // Get the country, make a copy, compute its coordinates
    let country = scales[countryCode] || {"scale": 1, "offset": [0, 0]};
    country = {...country}
    country.offset = country.offset.map(o => o * degreeSize * country.scale);
    country.offset[0] = country.offset[0] * -1;

    return country;
}
